/*
  The anchors passed via Base UI's `render` prop receive their content from the
  parent Button at runtime, so the static jsx-a11y/anchor-has-content check
  produces false positives here.
*/
/* eslint-disable jsx-a11y/anchor-has-content */
import type { LinkProps } from "@remix-run/react";
import type { ComponentProps } from "react";

import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { Link } from "@remix-run/react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/utils/cn";
import { isExternalLink } from "~/utils/is-external-link";

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-md border border-transparent bg-clip-padding text-sm font-medium tracking-wide focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground shadow-xs",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-9 gap-1.5 px-4 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-3 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        lg: "h-10 gap-1.5 px-6 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        xl: "h-12 text-base tracking-normal gap-1.5 px-8 has-data-[icon=inline-end]:pr-6 has-data-[icon=inline-start]:pl-6",
        icon: "size-9",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

type LinkButtonProps = Pick<
  ComponentProps<typeof Button>,
  "variant" | "size" | "className" | "disabled"
> &
  LinkProps;

function LinkButton({
  variant,
  size,
  className,
  disabled,
  to,
  reloadDocument,
  replace,
  state,
  preventScrollReset,
  relative,
  viewTransition,
  discover,
  prefetch,
  children,
  ...props
}: LinkButtonProps) {
  let buttonProps = {
    variant,
    size,
    className,
    disabled,
    nativeButton: false as const,
  };

  if (typeof to === "string" && isExternalLink(to)) {
    return (
      <Button {...buttonProps} render={<a href={to} {...props} />}>
        {children}
      </Button>
    );
  }

  return (
    <Button
      {...buttonProps}
      render={
        <Link
          to={to}
          reloadDocument={reloadDocument}
          replace={replace}
          state={state}
          preventScrollReset={preventScrollReset}
          relative={relative}
          viewTransition={viewTransition}
          discover={discover}
          prefetch={prefetch}
          {...props}
        />
      }
    >
      {children}
    </Button>
  );
}

export { Button, LinkButton, buttonVariants };
